import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { api, type SettingRow } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const KEY = "kill_switch";

/**
 * Renders only what the server last confirmed — never optimistically. A switch that looks
 * armed but is not would be the single worst lie this UI could tell (§21).
 */
export function KillSwitch() {
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["settings"],
    queryFn: api.settings,
  });

  const row = data?.settings.find((s: SettingRow) => s.key === KEY);
  const engaged = row?.value === true;

  const mutation = useMutation({
    mutationFn: (next: boolean) => api.putSetting(KEY, next),
    onSuccess: (res) => {
      queryClient.setQueryData(["settings"], (old: { settings: SettingRow[] } | undefined) => {
        if (!old) return old;
        return {
          settings: old.settings.map((s) => (s.key === KEY ? { ...s, value: res.value, stored: true } : s)),
        };
      });
      setConfirmOpen(false);
    },
  });

  return (
    <>
      <Button
        variant={engaged ? "destructive" : "outline"}
        size="lg"
        className={cn("w-full text-base font-semibold", engaged && "animate-pulse")}
        disabled={isLoading}
        onClick={() => setConfirmOpen(true)}
      >
        <AlertTriangle className="h-5 w-5" />
        {engaged ? "KILL SWITCH ENGAGED — trading blocked" : "Kill switch: off"}
      </Button>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{engaged ? "Disengage the kill switch?" : "Engage the kill switch?"}</DialogTitle>
            <DialogDescription>
              {engaged
                ? "Opening new positions and rule-driven management resume immediately."
                : "Blocks every new trade, approval and rule firing. Closing an existing position from the app still works — this stops the system from opening or managing, not from letting you get out."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button
              variant={engaged ? "default" : "destructive"}
              onClick={() => mutation.mutate(!engaged)}
              disabled={mutation.isPending}
            >
              {mutation.isPending ? "Working…" : engaged ? "Disengage" : "Engage kill switch"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
